import React from 'react';
import { VBox } from '../layout';
import type { ClassNameProp } from '../common-types';
import type { PatientDetailsPatientProp } from './internal';
import { PatientDetailsInsurancePlans } from './PatientDetailsInsurancePlans';
import { PatientDetailsPersonDetails } from './PatientDetailsPersonDetails';
import { PatientDetailsAvatarAndName } from './PatientDetailsAvatarAndName';
import { PatientDetailsContactInformation } from './PatientDetailsContactInformation';
import { PatientDetailsAddress } from './PatientDetailsAddress';
import type { PatientDetailsIdentifiersProps } from './PatientDetailsIdentifiers';
import { PatientDetailsIdentifiers } from './PatientDetailsIdentifiers';
import { PatientDetailsQRcode } from './PatientDetailsQRcode';

/**
 * Properties for a {@link PatientDetails} component.
 */
export type PatientDetailsProps = ClassNameProp &
  PatientDetailsPatientProp &
  Pick<PatientDetailsIdentifiersProps, 'filterIdentifiers'> & {
    /** If the QR code for the patient mobile app should be hidden. */
    mobileAppQRcodeHidden?: boolean;
    /** If the SSN is hidden. */
    ssnHidden?: boolean;
    /** If the address (and by extension the birthplace) is hidden. */
    addressHidden?: boolean;
    /** If the motherMaidenName part is hidden */
    motherMaidenNameHidden?: boolean;
    /** If the insurance details are hidden. */
    insuranceDetailsHidden?: boolean;
    /** If the contact information section is hidden. */
    contactInformationHidden?: boolean;
    /** If the identifiers section is hidden. */
    identifiersHidden?: boolean;
  };

/**
 * PatientDetails component used to display the details of an {@link EnhancedPatient}
 *
 * @param properties a {@link PatientDetailsProps} object
 * @return the created {@link JSX.Element}.
 * @constructor
 */
export function PatientDetails({
  patient,
  className = '',
  mobileAppQRcodeHidden = false,
  ssnHidden = false,
  addressHidden = false,
  motherMaidenNameHidden = false,
  insuranceDetailsHidden = false,
  contactInformationHidden = false,
  identifiersHidden = false,
  filterIdentifiers,
  ...props
}: Readonly<PatientDetailsProps>): JSX.Element {
  return (
    <VBox
      {...props}
      className={`ehs-patient-details ${className}`.trim()}
      aria-live="polite"
    >
      <PatientDetailsAvatarAndName patient={patient} />
      <PatientDetailsPersonDetails
        patient={patient}
        ssnHidden={ssnHidden}
        motherMaidenNameHidden={motherMaidenNameHidden}
        birthPlaceHidden={addressHidden}
      />
      {!insuranceDetailsHidden && (
        <PatientDetailsInsurancePlans patient={patient} />
      )}
      {!contactInformationHidden && (
        <PatientDetailsContactInformation patient={patient} />
      )}
      {!addressHidden && <PatientDetailsAddress patient={patient} />}
      {!identifiersHidden && (
        <PatientDetailsIdentifiers
          patient={patient}
          filterIdentifiers={filterIdentifiers}
        />
      )}
      {!mobileAppQRcodeHidden && <PatientDetailsQRcode patient={patient} />}
    </VBox>
  );
}
